import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Language {
  id: string;
  language_code: string;
  language_name: string;
  flag_emoji?: string;
  is_enabled: boolean;
  is_default: boolean;
}

interface TranslationContextType {
  currentLanguage: string;
  availableLanguages: Language[];
  defaultLanguage: string;
  setCurrentLanguage: (lang: string) => void;
  translateText: (text: string) => Promise<string>;
  isTranslating: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

// Cache for translations to avoid repeated API calls
const translationCache = new Map<string, string>();

export const TranslationProvider = ({ children }: TranslationProviderProps) => {
  const [currentLanguage, setCurrentLanguageState] = useState<string>('en');
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('en');
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    loadLanguages();
    // Load saved language preference
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage) {
      setCurrentLanguageState(savedLanguage);
    }
  }, []);

  const loadLanguages = async () => {
    try {
      console.log('Loading languages from database...');
      const { data, error } = await supabase
        .from('language_config')
        .select('*')
        .eq('is_enabled', true)
        .order('is_default', { ascending: false });

      if (error) throw error;

      console.log('Languages loaded:', data);
      setAvailableLanguages(data || []);
      
      // Find default language
      const defaultLang = data?.find(lang => lang.is_default);
      if (defaultLang) {
        console.log('Default language set to:', defaultLang.language_code);
        setDefaultLanguage(defaultLang.language_code);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  const setCurrentLanguage = (lang: string) => {
    setCurrentLanguageState(lang);
    localStorage.setItem('preferred-language', lang);
  };

  const translateText = async (text: string): Promise<string> => {
    // If current language is the same as default, return original text
    if (currentLanguage === defaultLanguage) {
      return text;
    }

    // Check cache first
    const cacheKey = `${text}_${currentLanguage}`;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey)!;
    }

    // If text is empty or just whitespace, return as is
    if (!text.trim()) {
      return text;
    }

    try {
      setIsTranslating(true);
      
      // Look for translation in database first
      const { data, error } = await supabase
        .from('translations')
        .select('translated_text')
        .eq('original_text', text)
        .eq('language_code', currentLanguage)
        .maybeSingle();

      if (!error && data?.translated_text) {
        const translatedText = data.translated_text;
        // Cache the translation
        translationCache.set(cacheKey, translatedText);
        return translatedText;
      }

      // If no translation found in database, register the text for future translation
      // but don't block the UI - just return original text
      if (!error) {
        console.log(`No translation found for "${text}" in ${currentLanguage}. Please add it in admin.`);
      }
      
      // Return original text if no translation found
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text; // Return original text on error
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        availableLanguages,
        defaultLanguage,
        setCurrentLanguage,
        translateText,
        isTranslating,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Hook for translating text with automatic updates
export const useTranslatedText = (text: string) => {
  const { translateText, currentLanguage, defaultLanguage } = useTranslation();
  const [translatedText, setTranslatedText] = useState(text);

  useEffect(() => {
    if (currentLanguage === defaultLanguage) {
      setTranslatedText(text);
      return;
    }

    let isCancelled = false;

    const translate = async () => {
      try {
        const translated = await translateText(text);
        if (!isCancelled) {
          setTranslatedText(translated);
        }
      } catch (error) {
        console.error('Translation error:', error);
        if (!isCancelled) {
          setTranslatedText(text); // Fallback to original text
        }
      }
    };

    translate();

    return () => {
      isCancelled = true;
    };
  }, [text, currentLanguage, defaultLanguage, translateText]);

  return translatedText;
};